import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Collapse,
  Paper
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  GetApp as GetAppIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  VerifiedUser as VerifiedUserIcon
} from '@mui/icons-material';
import { PieChart, Pie, ResponsiveContainer, Tooltip as RechartsTooltip, Cell } from 'recharts';
import scaApiService from '../../services/sca/ScaApiService';
import dashboardApiService from '../../services/dashboard/DashboardApiService';

const ExpandableRow = ({ check }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <React.Fragment>
      <TableRow hover onClick={() => setExpanded(!expanded)} sx={{ '& > *': { borderBottom: 'unset' }, cursor: 'pointer' }}>
        <TableCell sx={{ width: '5%' }}>
          <IconButton size="small">
            {expanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{check.id}</TableCell>
        <TableCell>{check.title}</TableCell>
        <TableCell>Command: {check.command}</TableCell>
        <TableCell>
          <Chip
            icon={check.result === 'passed' ? <CheckCircleIcon /> : <CancelIcon />}
            label={check.result}
            size="small"
            color={check.result === 'passed' ? 'success' : 'error'}
            sx={{ fontWeight: 'bold' }}
          />
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              <Paper elevation={3} sx={{ p: 3, background: '#f8fafc' }}>
                <Typography variant="h6" gutterBottom component="div" sx={{ fontWeight: 'bold', color: '#1e293b' }}>
                  Details for Check {check.id}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Rationale</Typography>
                    <Typography paragraph>{check.rationale}</Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Remediation</Typography>
                    <Typography paragraph>{check.remediation}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Description</Typography>
                    <Typography paragraph>{check.description}</Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Compliance</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {check.compliance?.map((comp, idx) => (
                        <Chip key={idx} label={`${comp.key.toUpperCase()}: ${comp.value}`} size="small" sx={{ backgroundColor: '#e0e7ff', color: '#3730a3' }} />
                      ))}
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};

const ScaDetails = () => {
  const { agentId } = useParams();
  const [searchParams] = useSearchParams();
  
  const policyId = searchParams.get('policy');
  
  const [scaData, setScaData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [agentInfo, setAgentInfo] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');

  useEffect(() => {
    fetchScaData();
    fetchAgentInfo();
  }, [agentId]);

  const fetchScaData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await scaApiService.getScaData(agentId);
      setScaData(data);
    } catch (err) {
      setError(`Failed to fetch SCA data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchAgentInfo = async () => {
    try {
      const agent = await dashboardApiService.getAgentById(agentId);
      setAgentInfo(agent);
      document.title = `SCA - Agent ${agentId} (${agent.name || 'Unknown'})`;
    } catch (err) {
      document.title = `SCA - Agent ${agentId}`;
    }
  };

  const getSelectedPolicy = () => {
    if (!scaData || !scaData.sca_summary) return null;
    const summary = scaData.sca_summary;
    if (!Array.isArray(summary)) {
      if (!policyId || summary.policy_id === policyId) return summary;
      return null;
    }
    if (!policyId) return summary[0] || null;
    return summary.find(policy => policy.policy_id === policyId) || null;
  };

  const policy = getSelectedPolicy();

  const filteredChecks = policy?.checks.filter(check => {
    const searchMatch = check.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        check.id.toString().includes(searchTerm);

    if (selectedFilter === 'all') {
      return searchMatch;
    }
    
    // Wazuh API can return 'not applicable' or 'not_applicable'
    if (selectedFilter === 'not_applicable') {
      return check.result.toLowerCase().replace(' ', '_') === 'not_applicable' && searchMatch;
    }

    return check.result.toLowerCase() === selectedFilter && searchMatch;
  }) || [];

  const paginatedChecks = filteredChecks.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading) return <CircularProgress />;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!policy) return <Alert severity="warning">Policy not found: {policyId}</Alert>;

  const formatDate = (timestamp) => {
    if (!timestamp || timestamp === 'N/A') return 'N/A';
    const date = new Date(timestamp);
    return `${date.toLocaleString('default', { month: 'short' })} ${date.getDate()}, ${date.getFullYear()} @ ${date.toLocaleTimeString()}`;
  }

  const pieData = [
    { name: 'Passed', value: policy.passed, color: '#16a34a' },
    { name: 'Failed', value: policy.failed, color: '#dc2626' },
    { name: 'Not Applicable', value: policy.not_applicable, color: '#d97706' },
  ];

  return (
    <Box sx={{ p: 3, background: '#f1f5f9', minHeight: '100vh' }}>
      <Typography variant="h4" sx={{ mb: 2, fontWeight: 'bold', color: '#1e293b' }}>
        <VerifiedUserIcon sx={{ mr: 1, verticalAlign: 'middle', color: '#6366f1' }} />
        {policy.benchmark}
      </Typography>
      
      <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)', borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Box sx={{ width: 150, height: 150, position: 'relative' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={70} dataKey="value" paddingAngle={5}>
                      {pieData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} />)}
                    </Pie>
                    <RechartsTooltip />
                  </PieChart>
                </ResponsiveContainer>
                <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1e293b' }}>{policy.score}%</Typography>
                  <Typography variant="caption" sx={{ color: '#64748b' }}>Score</Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={9}>
              <Grid container spacing={2} textAlign="center">
                <Grid item xs={12} sm={3}>
                  <Paper elevation={2} sx={{ p: 2, background: '#f0fdf4', borderLeft: '4px solid #16a34a' }}>
                    <Typography variant="h4" sx={{ color: '#16a34a', fontWeight: 'bold' }}>{policy.passed}</Typography>
                    <Typography sx={{ color: '#166534' }}>Passed</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Paper elevation={2} sx={{ p: 2, background: '#fef2f2', borderLeft: '4px solid #dc2626' }}>
                    <Typography variant="h4" sx={{ color: '#dc2626', fontWeight: 'bold' }}>{policy.failed}</Typography>
                    <Typography sx={{ color: '#991b1b' }}>Failed</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Paper elevation={2} sx={{ p: 2, background: '#fffbeb', borderLeft: '4px solid #d97706' }}>
                    <Typography variant="h4" sx={{ color: '#d97706', fontWeight: 'bold' }}>{policy.not_applicable}</Typography>
                    <Typography sx={{ color: '#92400e' }}>Not applicable</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Paper elevation={2} sx={{ p: 2, background: '#f8fafc', borderLeft: '4px solid #64748b' }}>
                    <Typography variant="h5" sx={{ color: '#334155', fontWeight: 'bold' }}>{formatDate(policy.end_scan)}</Typography>
                    <Typography sx={{ color: '#475569' }}>End scan</Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1e293b' }}>Checks ({filteredChecks.length})</Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Chip label="All" size="small" onClick={() => setSelectedFilter('all')} color={selectedFilter === 'all' ? 'primary' : 'default'} sx={{ cursor: 'pointer' }} />
              <Chip label="Passed" size="small" onClick={() => setSelectedFilter('passed')} color={selectedFilter === 'passed' ? 'success' : 'default'} sx={{ cursor: 'pointer' }} />
              <Chip label="Failed" size="small" onClick={() => setSelectedFilter('failed')} color={selectedFilter === 'failed' ? 'error' : 'default'} sx={{ cursor: 'pointer' }} />
              <Chip label="N/A" size="small" onClick={() => setSelectedFilter('not_applicable')} color={selectedFilter === 'not_applicable' ? 'warning' : 'default'} sx={{ cursor: 'pointer' }} />
              <Tooltip title="Refresh Data"><IconButton><RefreshIcon /></IconButton></Tooltip>
            </Box>
          </Box>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search by ID or title..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
          <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2 }}>
            <Table>
              <TableHead sx={{ background: '#f8fafc' }}>
                <TableRow>
                  <TableCell sx={{ width: '5%', fontWeight: 'bold' }} />
                  <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Title</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Target</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Result</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedChecks.map((check) => (
                  <ExpandableRow key={check.id} check={check} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={filteredChecks.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default ScaDetails;
